import { Trans } from "@lingui/react/macro";
import type { SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { useAccountTags, useSlimAccount } from "@/api/hooks/account";
import { duplicateDashboard } from "@/api/hooks/profile";
import { FloatingBoundaryProvider } from "@/components/context/FloatingBoundaryContext";
import { CardGroup } from "@/components/molecules/CardGroup/CardGroup";
import { DashboardCard } from "@/components/organisms/DashboardCard/DashboardCard";
import { DashboardHeader } from "@/dashboard/DashboardHeader";
import { DashboardListUIProvider, useDashboardListUI } from "@/dashboard/DashboardListUIProvider";
import { useDebounceQueryFilter } from "@/dashboard/hooks";
import type { DashboardProps, FilterAndSorterFormTypes } from "@/dashboard/types";
import { processProfiles, updateDashboardPreferences } from "@/dashboard/utils";
import { useProfileAndTabParams } from "@/hooks";
import { WindowKind, instanceId, useDesktop } from "@/overlay/components/window";
import { useTabManagement } from "@/overlay/overlays/default/NavigationHeader/hooks/useTabManagement";
import { useAppDispatch } from "@/reducer";
import { PROFILE_FETCH_STATUS, useProfiles } from "@/reducer/ProfileState";
import { selectedElementToRemove } from "@/reducer/UIState";
import { classNames } from "@/utility/jsx";
import { t } from "@lingui/core/macro";
import "./style.scss";

/**
 * Listing of profiles within the account
 */
export function Dashboards({ maximized, allowCollapse = false, isSideNav = false }: DashboardProps) {
  const { data: account } = useSlimAccount();
  const { tags } = useAccountTags();

  const methods = useForm<FilterAndSorterFormTypes>({
    defaultValues: { query: "", sort: { property: "title", isDescending: true } },
  });
  const [pinnedIds, setPinnedIds] = useState<Set<number>>(
    new Set(account?.preferences?.dashboard?.pinned_profiles || []),
  );
  const { profiles, fetchStatus } = useProfiles();

  const desktop = useDesktop();
  const dispatch = useAppDispatch();
  const { handleTabSelect } = useTabManagement();

  const activeSorter = useWatch({ control: methods.control, name: "sort" });
  const query = useDebounceQueryFilter(methods.control);
  const profilesArray = useMemo<SlimProfile[]>(() => Object.values(profiles), [profiles]);

  useEffect(() => {
    if (account?.preferences?.dashboard) {
      const prefs = account.preferences.dashboard;

      if (prefs.pinned_profiles) {
        setPinnedIds(new Set(prefs.pinned_profiles));
      }

      if (prefs.sorting_option && prefs.sorting_direction) {
        methods.setValue("sort", {
          property: prefs.sorting_option as keyof SlimProfile,
          isDescending: prefs.sorting_direction === "desc",
        });
      }
    }
  }, [account?.preferences?.dashboard, methods]);

  // Main dashboard filtering pipeline: debounced query (dashboard title + tab titles), tags, sort and pin priority.
  const processedProfiles = useMemo(
    () => processProfiles(profilesArray, query, tags.active || [], activeSorter, pinnedIds),
    [profilesArray, query, activeSorter, pinnedIds, tags.active],
  );

  const handleSortChange = (newSort: FilterAndSorterFormTypes["sort"]) => {
    methods.setValue("sort", newSort);
    if (account) {
      updateDashboardPreferences(account, { sorting: newSort });
    }
  };

  const handleTogglePin = (profileId: number) => {
    if (!account) {
      return;
    }

    setPinnedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(profileId)) {
        newSet.delete(profileId);
      } else {
        newSet.add(profileId);
      }

      updateDashboardPreferences(account, { pinnedIds: newSet });

      return newSet;
    });
  };

  const handleEditTitle = (profile: SlimProfile) => {
    desktop.dispatch({ type: "toggleWindowVisibility", windowId: instanceId(WindowKind.RenameDashboard) });
    desktop.dispatch({
      type: "setWindowCustomProps",
      windowId: instanceId(WindowKind.RenameDashboard),
      windowKind: WindowKind.RenameDashboard,
      customProps: { profile },
    });
  };

  const handleDelete = (profileId: number) => {
    const profile = profiles[profileId];
    if (!profile) {
      console.error(`Profile with ID ${profileId} not found.`);
      return;
    }
    const title = profile.title || "Untitled Dashboard";

    dispatch(
      selectedElementToRemove({
        type: "profile",
        item: profile,
        label: t`Do you want to delete profile "${title}"?`,
      }),
    );
    desktop.dispatch({
      type: "setWindowVisibility",
      windowId: instanceId(WindowKind.DeleteConfirmation),
      isVisible: true,
    });
  };

  const handleDuplicate = (profileId: number) => {
    dispatch(duplicateDashboard({ profileId: profileId }));
  };

  const dashboardsBoundaryRef = useRef<HTMLDivElement>(null);

  if (fetchStatus === PROFILE_FETCH_STATUS.idle || fetchStatus === PROFILE_FETCH_STATUS.loading) {
    return <mm-loader size="xlarge" />;
  }
  if (fetchStatus === PROFILE_FETCH_STATUS.failed) {
    return <Trans id="profile.fetch.failure">Failed to load profiles from server</Trans>;
  }
  const dashboardContent = (
    <>
      <DashboardHeader showCollapseButton={allowCollapse} isSideNav={isSideNav} onSortChange={handleSortChange} />
      <FloatingBoundaryProvider value={dashboardsBoundaryRef}>
        <div
          ref={dashboardsBoundaryRef}
          className={classNames("dashboards__content", !maximized && "box-section--breakout")}
        >
          {processedProfiles.length ? (
            <ProfilesGrid
              profiles={processedProfiles}
              allowCollapse={allowCollapse}
              pinnedIds={pinnedIds}
              onTogglePin={handleTogglePin}
              onDelete={handleDelete}
              onEditTitle={handleEditTitle}
              onTabSelect={(profileId, tabId) => handleTabSelect(profiles[profileId], tabId)}
              onDuplicate={handleDuplicate}
              maximized={maximized}
            />
          ) : (
            // TODO add nice looking "no profiles found" page
            <Trans id="profile.empty">No dashboards found. Please add one!</Trans>
          )}
        </div>
      </FloatingBoundaryProvider>
    </>
  );

  return (
    <FormProvider {...methods}>
      <DashboardListUIProvider>
        {maximized ? dashboardContent : <div className="profile-area-content box-section">{dashboardContent}</div>}
      </DashboardListUIProvider>
    </FormProvider>
  );
}

type ProfilesGridProps = {
  profiles: SlimProfile[];
  pinnedIds: Set<number>;
  allowCollapse: boolean;
  onTogglePin: (profileId: number) => void;
  onDelete: (profileId: number) => void;
  onEditTitle: (profile: SlimProfile) => void;
  onTabSelect: (profileId: number, tabId: number) => void;
  onDuplicate: (profileId: number) => void;
  maximized?: boolean | undefined;
};

const ProfilesGrid: React.FC<ProfilesGridProps> = ({
  profiles,
  pinnedIds,
  onTogglePin,
  onDelete,
  onEditTitle,
  onTabSelect,
  onDuplicate,
  maximized,
  allowCollapse,
}) => {
  // Only in sidenav can be expandAll used
  const { expandAll } = useDashboardListUI();
  const { profileId } = useProfileAndTabParams();
  return (
    <CardGroup layout="grid">
      {profiles.map((profile) => (
        <DashboardCard
          key={profile.id.toString()}
          profile={profile}
          activeDashboard={profileId}
          isPinned={pinnedIds.has(profile.id)}
          onPin={() => onTogglePin(profile.id)}
          isExpanded={allowCollapse ? expandAll : false}
          onDelete={(id) => onDelete(Number.parseInt(id, 10))}
          onEditTitle={onEditTitle}
          onTabSelect={onTabSelect}
          onDuplicate={onDuplicate}
        />
      ))}
    </CardGroup>
  );
};
